import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Pill } from '@/components/pills/Pill'
import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import {
  fetchSupportAccess,
  fetchSupportAccessAudit,
  openSupportAccess,
  closeSupportAccess,
} from '@/reducers/supportAccess/supportAccess.actions'
import { formatCountdown } from '@/utils/formatCountdown'
import { formatTime } from '@/utils/helpers'

const POLL_INTERVAL_MS = 60_000

const ERROR_COPY = {
  fetchFailed: 'Could not load support access status.',
  openFailed: 'Could not open the support access window.',
  closeFailed: 'Could not close the support access window.',
}

// core-service sends `extended` on opened rows, true only when the window it
// replaced was still live. It also derives `support_access_expired` rows for
// windows that lapsed on their own, which nothing records an audit event for.
const getActionLabel = (eventType, row) => {
  if (eventType === 'support_access_closed') {
    return 'Closed'
  }
  if (eventType === 'support_access_expired') {
    return 'Expired'
  }
  if (eventType === 'support_access_opened') {
    return row?.extended ? 'Extended' : 'Opened'
  }
  return eventType
}

const AUDIT_COLUMNS = [
  {
    accessor: 'event_type',
    label: 'Action',
    isSearchable: false,
    format: (eventType, row) => (
      <Pill
        small
        success={eventType === 'support_access_opened'}
        informative={eventType === 'support_access_expired'}
      >
        {getActionLabel(eventType, row)}
      </Pill>
    ),
  },
  {
    accessor: 'author',
    label: 'Who',
    isSearchable: false,
  },
  {
    accessor: 'created_at',
    label: 'When',
    isSearchable: false,
    format: (createdAt) => formatTime(createdAt),
  },
]

const errorContent = (error) => {
  if (!error) {
    return null
  }
  const base = ERROR_COPY[error.kind] || 'Something went wrong.'
  return error.detail ? `${base} (${error.detail})` : base
}

const SupportAccessPage = () => {
  const dispatch = useDispatch()
  const { supportAccessUntil, isOpen, expiresInSeconds, isLoading, error, auditLog } = useSelector(
    (state) => state.supportAccess
  )
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)

  const [liveSeconds, setLiveSeconds] = useState(expiresInSeconds)
  const countdownRef = useRef(null)

  useEffect(() => {
    dispatch(fetchSupportAccess(currentUserOrgId))
    dispatch(fetchSupportAccessAudit(currentUserOrgId))

    const pollInterval = setInterval(() => {
      dispatch(fetchSupportAccess(currentUserOrgId))
    }, POLL_INTERVAL_MS)

    return () => clearInterval(pollInterval)
  }, [dispatch, currentUserOrgId])

  useEffect(() => {
    setLiveSeconds(expiresInSeconds)
  }, [expiresInSeconds])

  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
    }

    if (isOpen && !isNil(expiresInSeconds) && expiresInSeconds > 0) {
      countdownRef.current = setInterval(() => {
        setLiveSeconds((previous) => {
          if (previous <= 1) {
            clearInterval(countdownRef.current)
            return 0
          }
          return previous - 1
        })
      }, 1000)
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
      }
    }
  }, [isOpen, expiresInSeconds])

  // The countdown hitting zero means the window lapsed while the page was open.
  // Re-fetch so the status flips to Closed now rather than holding an "Open
  // until <past date> / Expires in 0s" pill until the next poll. Going through
  // the server keeps it authoritative: a skewed client clock self-corrects on
  // the response instead of showing Closed on a still-live window.
  useEffect(() => {
    if (isOpen && liveSeconds === 0) {
      dispatch(fetchSupportAccess(currentUserOrgId))
    }
  }, [dispatch, isOpen, liveSeconds, currentUserOrgId])

  const handleOpenAccess = async () => {
    await dispatch(openSupportAccess(currentUserOrgId))
    dispatch(fetchSupportAccessAudit(currentUserOrgId))
  }

  const handleCloseAccess = async () => {
    await dispatch(closeSupportAccess(currentUserOrgId))
    dispatch(fetchSupportAccessAudit(currentUserOrgId))
  }

  const localizedExpiry = supportAccessUntil ? new Date(supportAccessUntil).toLocaleString() : null

  return (
    <div data-testid="support-access-page">
      <PageHeader title="Balto Support Access" />

      <Banner informative>
        <p>
          Opening a support access window allows Balto employees to temporarily view
          normally-redacted data for your organization, including customer IDs and call transcripts,
          so they can diagnose issues on your behalf. The window automatically expires after 24
          hours, and you can close it at any time. Every open and close action is recorded in the
          audit log below, along with any window that expired on its own.
        </p>
      </Banner>

      {error && <ErrorMessage content={errorContent(error)} data-testid="support-access-error" />}

      <div className="flex-align-center gap mt" data-testid="support-access-status">
        <span className="bold">Status:</span>
        {isOpen ? (
          <Pill success data-testid="support-access-pill-open">
            Open until {localizedExpiry}
          </Pill>
        ) : (
          <Pill data-testid="support-access-pill-closed">Closed</Pill>
        )}

        {isOpen && !isNil(liveSeconds) && (
          <span className="muted-text" data-testid="support-access-countdown">
            Expires in {formatCountdown(liveSeconds)}
          </span>
        )}
      </div>

      <div className="form-actions left-aligned mt">
        {isOpen ? (
          <>
            <Button
              critical
              onClick={handleCloseAccess}
              loading={isLoading}
              data-testid="support-access-close-button"
            >
              Close Now
            </Button>
            <Button
              basic
              onClick={handleOpenAccess}
              loading={isLoading}
              data-testid="support-access-extend-button"
            >
              Reset to 24 hours
            </Button>
          </>
        ) : (
          <Button
            brand
            onClick={handleOpenAccess}
            loading={isLoading}
            data-testid="support-access-open-button"
          >
            Open Balto Support Access
          </Button>
        )}
      </div>

      <h3 className="mt">Audit Log</h3>
      <Card className="no-padding" data-testid="support-access-audit-table">
        <AdvancedTable
          columns={AUDIT_COLUMNS}
          rows={auditLog}
          sortEnabled={false}
          noDataText="No audit records yet"
        />
      </Card>
    </div>
  )
}

export default SupportAccessPage
